import { useMemo } from "react";
import {
  Activity,
  AlertCircle,
  Package,
  Route as RouteIcon,
  Users,
} from "lucide-react";
import { type Task, TripStatus } from "@/api.types.v2";
import { useTasks, taskSearchQueries } from "@/api/tasks";
import { useActiveTrips } from "@/api/trips";
import { useOperators } from "@/api/operators";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { useDispatchDateStore } from "@/features/dashboard/dispatch-date.store";
import { useNow } from "@/features/dashboard/use-now";
import { applyTaskScopeFilter } from "@/features/dashboard/task-urgency";

type Tone = "orange" | "red" | "indigo" | "zinc" | "green";

const TONE_TEXT: Record<Tone, string> = {
  orange: "text-orange-600",
  red: "text-red-600",
  indigo: "text-indigo-600",
  zinc: "text-zinc-800",
  green: "text-green-600",
};

function Kpi({
  icon,
  value,
  label,
  tone,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  tone: Tone;
}) {
  return (
    <div className="flex h-full items-center gap-2 px-3.5">
      <span className={`shrink-0 ${TONE_TEXT[tone]}`}>{icon}</span>
      <div className="leading-none">
        <span
          className={`text-[16px] font-bold tabular-nums ${TONE_TEXT[tone]}`}
        >
          {value}
        </span>
        <span className="ml-1.5 whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-zinc-400">
          {label}
        </span>
      </div>
    </div>
  );
}

export function KpiStrip() {
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const scope = {
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  };
  const selectedDate = useDispatchDateStore((s) => s.selectedDate);
  const now = useNow(60_000);

  const { data: pickups } = useTasks({
    ...taskSearchQueries.activePickupTasks,
    ...scope,
  });
  const { data: deliveries } = useTasks({
    ...taskSearchQueries.activeDeliveryTasks,
    ...scope,
  });
  const { data: dropoffs } = useTasks({
    ...taskSearchQueries.activeCarrierDropoffTasks,
    ...scope,
  });
  const { data: returns } = useTasks({
    ...taskSearchQueries.activeReturnTasks,
    ...scope,
  });

  const allUnassigned = useMemo<Task[]>(() => {
    const lists = [pickups, deliveries, dropoffs, returns];
    return lists.flatMap((r) => r?.data ?? []).filter((t) => !t.tripId);
  }, [pickups, deliveries, dropoffs, returns]);

  const unassignedCount = useMemo(
    () => applyTaskScopeFilter(allUnassigned, selectedDate, null, now).length,
    [allUnassigned, selectedDate, now]
  );
  const overdueCount = useMemo(
    () =>
      applyTaskScopeFilter(allUnassigned, selectedDate, "overdue", now).length,
    [allUnassigned, selectedDate, now]
  );

  const { data: tripsResponse } = useActiveTrips(scope);
  const trips = tripsResponse?.data ?? [];
  const activeTrips = trips.filter(
    (t) => t.status === TripStatus.ACTIVE
  ).length;
  const plannedTrips = trips.filter(
    (t) => t.status === TripStatus.PLANNED
  ).length;

  const { data: operatorsResponse } = useOperators({ ...scope, limit: 200 });
  const operators = operatorsResponse?.data ?? [];
  const onDuty = operators.filter((o) => o.status === "ON_DUTY").length;

  return (
    <div className="z-20 flex h-10 shrink-0 items-stretch divide-x divide-zinc-100 border-b border-zinc-200 bg-white">
      <Kpi
        icon={<Package className="h-4 w-4" />}
        value={unassignedCount}
        label="Unassigned"
        tone="orange"
      />
      <Kpi
        icon={<AlertCircle className="h-4 w-4" />}
        value={overdueCount}
        label="Overdue"
        tone="red"
      />
      <Kpi
        icon={<RouteIcon className="h-4 w-4" />}
        value={activeTrips}
        label="Active trips"
        tone="indigo"
      />
      <Kpi
        icon={<Activity className="h-4 w-4" />}
        value={plannedTrips}
        label="Planned"
        tone="zinc"
      />
      <Kpi
        icon={<Users className="h-4 w-4" />}
        value={`${onDuty}/${operators.length}`}
        label="On duty"
        tone="green"
      />
      <div className="flex-1" />
    </div>
  );
}
